import React, { useState, useEffect, useRef } from 'react';
import axiosInstance from '../../apis/axiosConfig';
import { toast } from 'react-toastify';
import { useParams, useNavigate } from 'react-router-dom';
import { FaCar, FaArrowLeft, FaDollarSign, FaTachometerAlt, FaUser, FaInfoCircle, FaShieldAlt } from 'react-icons/fa';
import '../../pages_premium.css';
import './AddCar.css';
import { getFullImageUrl } from '../../utils/url';
import { compressImageFile } from '../../utils/imageCompressor';

const safeParseJson = (val) => {
  if (!val) return null;
  if (typeof val === 'object') return val;
  try {
    const parsed = typeof val === 'string' ? JSON.parse(val) : val;
    if (typeof parsed === 'object') return parsed;
  } catch (e) {
    if (typeof val === 'string') return { info: val };
  }
  return null;
};

const safeParseImages = (imagesVal) => {
  if (!imagesVal) return [];
  if (Array.isArray(imagesVal)) return imagesVal;
  try {
    const parsed = typeof imagesVal === 'string' ? JSON.parse(imagesVal) : imagesVal;
    if (Array.isArray(parsed)) return parsed;
  } catch (e) {
    console.warn('Failed to parse features/images', e);
  }
  return [];
};

function UpdateCar() {
    const { id } = useParams();
    const navigate = useNavigate();
    const fileInputRef = useRef(null);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(true);
    const [activeField, setActiveField] = useState(null);
    const [formData, setFormData] = useState({
        owner_id: '',
        title: '',
        make: '',
        model: '',
        variant: '',
        type: 'Sedan',
        year: '',
        price: '',
        period: 'day',
        street: '',
        city: '',
        state: 'NSW',
        postcode: '',
        transmission: 'Automatic',
        fuelType: 'Petrol',
        seats: '5',
        mileageLimit: 'Unlimited',
        insuranceIncluded: true,
        features: [],
        verification: {
            regoNumber: '',
            regoState: 'NSW',
            regoExpiry: '',
            vin: '',
            insuranceProvider: '',
            insuranceExpiry: ''
        },
        images: [],
        shortDesc: '',
        description: '',
        contactName: '',
        phone: '',
        email: '',
        car_category: 'per_day',
        status: 'available'
    });

    useEffect(() => {
        const loadData = async () => {
            try {
                // Fetch users list
                const usersRes = await axiosInstance.get('/admin/users');
                if (Array.isArray(usersRes.data)) {
                    setUsers(usersRes.data);
                }

                // Fetch car details
                const carRes = await axiosInstance.get(`/cars/${id}`);
                const car = carRes.data;
                const verifObj = safeParseJson(car.verification) || {};

                setFormData({
                    owner_id: car.owner_id || '',
                    title: car.title || '',
                    make: car.make || '',
                    model: car.model || '',
                    variant: car.variant || '',
                    type: car.type || 'Sedan',
                    year: car.year || '',
                    price: car.price || '',
                    period: car.period || 'day',
                    street: car.street || '',
                    city: car.city || '',
                    state: car.state || 'NSW',
                    postcode: car.postcode || '',
                    transmission: car.transmission || 'Automatic',
                    fuelType: car.fuelType || 'Petrol',
                    seats: String(car.seats || '5'),
                    mileageLimit: car.mileageLimit || 'Unlimited',
                    insuranceIncluded: car.insuranceIncluded !== undefined ? car.insuranceIncluded : true,
                    features: safeParseImages(car.features),
                    verification: {
                        regoNumber: verifObj.regoNumber || '',
                        regoState: verifObj.regoState || 'NSW',
                        regoExpiry: verifObj.regoExpiry || '',
                        vin: verifObj.vin || '',
                        insuranceProvider: verifObj.insuranceProvider || '',
                        insuranceExpiry: verifObj.insuranceExpiry || ''
                    },
                    images: safeParseImages(car.images),
                    shortDesc: car.shortDesc || '',
                    description: car.description || '',
                    contactName: car.contactName || '',
                    phone: car.phone || '',
                    email: car.email || '',
                    car_category: car.car_category || 'per_day',
                    status: car.status || 'available'
                });
            } catch (error) {
                console.error('Error loading car details:', error);
                toast.error('Failed to load details');
            } finally {
                setFetching(false);
            }
        };
        loadData();
    }, [id]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleVerificationChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            verification: {
                ...(prev.verification || {}),
                [name]: value
            }
        }));
    };

    const handleFeatureCheckbox = (featureName, isChecked) => {
        setFormData(prev => ({
            ...prev,
            features: isChecked 
                ? [...prev.features, featureName]
                : prev.features.filter(f => f !== featureName)
        }));
    };

    const MAX_PHOTOS = 3;
    const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10MB

    const handleFileChange = (e) => {
        const selectedFiles = Array.from(e.target.files || []);
        if (selectedFiles.length === 0) return;

        const oversized = selectedFiles.filter(f => f.size > MAX_FILE_SIZE_BYTES);
        if (oversized.length > 0) {
            toast.error(`Each photo must be under 10 MB! Oversized: ${oversized.map(f => f.name).join(', ')}`);
        }

        const validFiles = selectedFiles.filter(f => f.size <= MAX_FILE_SIZE_BYTES);
        if (validFiles.length === 0) {
            e.target.value = '';
            return;
        }

        const currentCount = formData.images?.length || 0;
        const remainingSlots = MAX_PHOTOS - currentCount;
        if (remainingSlots <= 0) {
            toast.warning(`Maximum ${MAX_PHOTOS} photos are allowed per car listing!`);
            e.target.value = '';
            return;
        }

        let filesToProcess = validFiles;
        if (validFiles.length > remainingSlots) {
            toast.warning(`Only ${MAX_PHOTOS} photos allowed! Added the first ${remainingSlots} photo(s).`);
            filesToProcess = validFiles.slice(0, remainingSlots);
        }

        Promise.all(filesToProcess.map(file => compressImageFile(file))).then(results => {
            const dataUrls = results.map(r => r.dataUrl).filter(Boolean);
            setFormData(prev => ({
                ...prev,
                images: [...prev.images, ...dataUrls].slice(0, MAX_PHOTOS)
            }));
        });
        e.target.value = '';
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await axiosInstance.put(`/cars/${id}`, formData);
            toast.success('🚗 Car details updated successfully!');
            navigate('/cars');
        } catch (error) {
            console.error('Error updating car:', error);
            toast.error(error.response?.data?.message || 'Failed to update car');
        } finally {
            setLoading(false);
        }
    };

    if (fetching) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '70vh' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="sr-only">Loading Details...</span>
                </div>
            </div>
        );
    }

    const fillPercent = Object.values(formData).filter(Boolean).length / Object.keys(formData).length * 100;

    return (
        <div className="page-premium-wrapper">
            <div className="page-premium-content">

                {/* Hero Header */}
                <div className="page-hero-header page-hero-properties" style={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)' }}>
                    <div className="ph-blob ph-blob-1"></div>
                    <div className="ph-blob ph-blob-2"></div>

                    <div className="ph-left">
                        <div className="ph-icon">
                            <FaCar size={28} />
                        </div>
                        <div>
                            <h1 className="ph-title">Edit Car Listing</h1>
                            <p className="ph-subtitle">Modify specifications, pricing, location, and images of the listing</p>
                        </div>
                    </div>

                    <div className="ph-right">
                        <div className="ph-count-badge">
                            <span className="count-num">{Math.round(fillPercent)}%</span>
                            <span className="count-lbl">Form Filled</span>
                        </div>
                    </div>
                </div>

                {/* Back button */}
                <div className="mb-3">
                    <button className="ac-back-btn" onClick={() => navigate('/cars')}>
                        <FaArrowLeft /> Back to List
                    </button>
                </div>

                {/* Form Card */}
                <div className="ac-form-card">
                    <form onSubmit={handleSubmit}>

                        {/* SECTION 1: Owner Info */}
                        <div className="ac-section">
                            <div className="ac-section-header">
                                <div className="ac-section-icon"><FaUser /></div>
                                <div>
                                    <h2>Owner Details</h2>
                                    <p>Select the registered user representing this listing</p>
                                </div>
                            </div>
                            <div className="ac-field-group">
                                <div className={`ac-field-wrap ac-field-full ${activeField === 'owner_id' ? 'ac-field-active' : ''} ${formData.owner_id ? 'ac-field-filled' : ''}`}>
                                    <label htmlFor="owner_id">Listed Owner *</label>
                                    <select
                                        id="owner_id"
                                        name="owner_id"
                                        value={formData.owner_id}
                                        onChange={handleChange}
                                        onFocus={() => setActiveField('owner_id')}
                                        onBlur={() => setActiveField(null)}
                                        required
                                    >
                                        <option value="">-- Choose Owner --</option>
                                        {users.map(u => (
                                            <option key={u.id} value={u.id}>
                                                {u.name} ({u.email}) · {u.role}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>

                        <div className="ac-divider"></div>

                        {/* SECTION 2: Basic Info */}
                        <div className="ac-section">
                            <div className="ac-section-header">
                                <div className="ac-section-icon"><FaCar /></div>
                                <div>
                                    <h2>Specifications & Details</h2>
                                    <p>Year, make, model and car body type details</p>
                                </div>
                            </div>
                            <div className="ac-field-group">
                                <div className={`ac-field-wrap ac-field-full ${activeField === 'title' ? 'ac-field-active' : ''} ${formData.title ? 'ac-field-filled' : ''}`}>
                                    <label htmlFor="title">Listing Title *</label>
                                    <input type="text" id="title" name="title" value={formData.title} onChange={handleChange} required placeholder="e.g. 2023 Toyota Corolla ZR" />
                                </div>
                                <div className="ac-field-wrap ac-field-full">
                                    <label htmlFor="car_category">
                                        🇦🇺 Vehicle Category (Australia Standard) *
                                    </label>
                                    <select
                                        id="car_category"
                                        name="car_category"
                                        value={formData.car_category || 'per_day'}
                                        onChange={handleChange}
                                        required
                                        style={{ fontSize: '13px', fontWeight: 600 }}
                                    >
                                        <option value="per_day">🚗 Per Day / Short-Term Rental (Flexible 1-30+ Days)</option>
                                        <option value="uber_eats">🍔 Uber Eats / Delivery (Food & Parcels • Min 1 Month Rental)</option>
                                        <option value="uber_taxi">🚕 Uber / Taxi (Rideshare Transport • Min 1 Month Rental)</option>
                                        <option value="pickup_van">🛻 Ute / Pickup / Van (Tradies & Cargo • Min 1 Month Rental)</option>
                                    </select>
                                    <div className="ac-field-hint" style={{ color: formData.car_category !== 'per_day' ? '#b45309' : '#0369a1' }}>
                                        <FaInfoCircle size={11} /> 
                                        {formData.car_category && formData.car_category !== 'per_day'
                                            ? 'Australia Rule: Commercial rideshare, delivery, and ute/van listings require a minimum 1-month rental contract.'
                                            : 'Flexible: Can be rented per day (1, 10, 20 days) or any custom period.'}
                                    </div>
                                </div>
                                <div className={`ac-field-wrap ${activeField === 'type' ? 'ac-field-active' : ''} ${formData.type ? 'ac-field-filled' : ''}`}>
                                    <label htmlFor="type">Car Type *</label>
                                    <select id="type" name="type" value={formData.type} onChange={handleChange} required>
                                        {['Sedan', 'SUV', 'Hatchback', 'Coupe', 'Convertible', 'Truck', 'Van'].map(t => (
                                            <option key={t} value={t}>{t}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className={`ac-field-wrap ${activeField === 'make' ? 'ac-field-active' : ''} ${formData.make ? 'ac-field-filled' : ''}`}>
                                    <label htmlFor="make">Make *</label>
                                    <input type="text" id="make" name="make" value={formData.make} onChange={handleChange} required />
                                </div>
                                <div className={`ac-field-wrap ${activeField === 'model' ? 'ac-field-active' : ''} ${formData.model ? 'ac-field-filled' : ''}`}>
                                    <label htmlFor="model">Model *</label>
                                    <input type="text" id="model" name="model" value={formData.model} onChange={handleChange} required />
                                </div>
                                <div className={`ac-field-wrap ${activeField === 'variant' ? 'ac-field-active' : ''} ${formData.variant ? 'ac-field-filled' : ''}`}>
                                    <label htmlFor="variant">Variant</label>
                                    <input type="text" id="variant" name="variant" value={formData.variant} onChange={handleChange} />
                                </div>
                                <div className={`ac-field-wrap ${activeField === 'year' ? 'ac-field-active' : ''} ${formData.year ? 'ac-field-filled' : ''}`}>
                                    <label htmlFor="year">Year *</label>
                                    <input type="number" id="year" name="year" value={formData.year} onChange={handleChange} required />
                                </div>
                                <div className={`ac-field-wrap ${activeField === 'seats' ? 'ac-field-active' : ''} ${formData.seats ? 'ac-field-filled' : ''}`}>
                                    <label htmlFor="seats">Seats Count *</label>
                                    <input type="number" id="seats" name="seats" value={formData.seats} onChange={handleChange} required />
                                </div>
                            </div>
                        </div>

                        <div className="ac-divider"></div>

                        {/* SECTION 3: Technical Specifications */}
                        <div className="ac-section">
                            <div className="ac-section-header">
                                <div className="ac-section-icon"><FaTachometerAlt /></div>
                                <div>
                                    <h2>Technical Specs</h2>
                                    <p>Transmission, fuel and mileage specifications</p>
                                </div>
                            </div>
                            <div className="ac-field-group ac-field-group-3">
                                <div className={`ac-field-wrap ${activeField === 'transmission' ? 'ac-field-active' : ''} ${formData.transmission ? 'ac-field-filled' : ''}`}>
                                    <label htmlFor="transmission">Transmission *</label>
                                    <select id="transmission" name="transmission" value={formData.transmission} onChange={handleChange} required>
                                        <option value="Automatic">Automatic</option>
                                        <option value="Manual">Manual</option>
                                        <option value="CVT">CVT</option>
                                    </select>
                                </div>
                                <div className={`ac-field-wrap ${activeField === 'fuelType' ? 'ac-field-active' : ''} ${formData.fuelType ? 'ac-field-filled' : ''}`}>
                                    <label htmlFor="fuelType">Fuel Type *</label>
                                    <select id="fuelType" name="fuelType" value={formData.fuelType} onChange={handleChange} required>
                                        <option value="Petrol">Petrol</option>
                                        <option value="Diesel">Diesel</option>
                                        <option value="Hybrid">Hybrid</option>
                                        <option value="Electric">Electric</option>
                                        <option value="LPG">LPG</option>
                                    </select>
                                </div>
                                <div className={`ac-field-wrap ${activeField === 'mileageLimit' ? 'ac-field-active' : ''} ${formData.mileageLimit ? 'ac-field-filled' : ''}`}>
                                    <label htmlFor="mileageLimit">Mileage Limit *</label>
                                    <input type="text" id="mileageLimit" name="mileageLimit" value={formData.mileageLimit} onChange={handleChange} required />
                                </div>
                            </div>
                        </div>

                        <div className="ac-divider"></div>

                        {/* SECTION 4: Pricing & Location */}
                        <div className="ac-section">
                            <div className="ac-section-header">
                                <div className="ac-section-icon ac-section-icon-green"><FaDollarSign /></div>
                                <div>
                                    <h2>Pricing &amp; Address</h2>
                                    <p>Set daily/weekly rates and location address</p>
                                </div>
                            </div>
                            <div className="ac-field-group">
                                <div className={`ac-field-wrap ${activeField === 'price' ? 'ac-field-active' : ''} ${formData.price ? 'ac-field-filled' : ''}`}>
                                    <label htmlFor="price">Rental Rate ($) *</label>
                                    <input type="number" id="price" name="price" value={formData.price} onChange={handleChange} required />
                                </div>
                                <div className={`ac-field-wrap ${activeField === 'period' ? 'ac-field-active' : ''} ${formData.period ? 'ac-field-filled' : ''}`}>
                                    <label htmlFor="period">Rent Period *</label>
                                    <select id="period" name="period" value={formData.period} onChange={handleChange} required>
                                        <option value="day">per Day</option>
                                        <option value="week">per Week</option>
                                        <option value="month">per Month</option>
                                    </select>
                                </div>
                                <div className={`ac-field-wrap ${activeField === 'street' ? 'ac-field-active' : ''} ${formData.street ? 'ac-field-filled' : ''}`}>
                                    <label htmlFor="street">Street Address</label>
                                    <input type="text" id="street" name="street" value={formData.street} onChange={handleChange} />
                                </div>
                                <div className={`ac-field-wrap ${activeField === 'city' ? 'ac-field-active' : ''} ${formData.city ? 'ac-field-filled' : ''}`}>
                                    <label htmlFor="city">City *</label>
                                    <input type="text" id="city" name="city" value={formData.city} onChange={handleChange} required />
                                </div>
                                <div className={`ac-field-wrap ${activeField === 'state' ? 'ac-field-active' : ''} ${formData.state ? 'ac-field-filled' : ''}`}>
                                    <label htmlFor="state">State *</label>
                                    <select id="state" name="state" value={formData.state} onChange={handleChange} required>
                                        {['NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'ACT', 'NT'].map(st => (
                                            <option key={st} value={st}>{st}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className={`ac-field-wrap ${activeField === 'postcode' ? 'ac-field-active' : ''} ${formData.postcode ? 'ac-field-filled' : ''}`}>
                                    <label htmlFor="postcode">Postcode</label>
                                    <input type="text" id="postcode" name="postcode" value={formData.postcode} onChange={handleChange} />
                                </div>
                            </div>
                        </div>

                        <div className="ac-divider"></div>

                        {/* SECTION 5: Features */}
                        <div className="ac-section">
                            <div className="ac-section-header">
                                <div className="ac-section-icon"><FaInfoCircle /></div>
                                <div>
                                    <h2>Features &amp; Extras</h2>
                                    <p>Additional features and rental insurance options</p>
                                </div>
                            </div>
                            <div className="ac-field-group ac-field-full">
                                <div className="mb-4 d-flex align-items-center gap-3 w-100">
                                    <input
                                        type="checkbox"
                                        id="insuranceIncluded"
                                        name="insuranceIncluded"
                                        checked={formData.insuranceIncluded}
                                        onChange={handleChange}
                                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                    />
                                    <label htmlFor="insuranceIncluded" className="m-0 font-weight-bold text-dark" style={{ cursor: 'pointer' }}>
                                        Rental Insurance is Included
                                    </label>
                                </div>

                                <div className="w-100">
                                    <label htmlFor="insuranceIncluded" className="text-uppercase text-muted font-weight-bold small tracking-wider mb-2 d-block">Additional Amenities</label>
                                    <div className="row w-100 m-0">
                                        {['Bluetooth', 'GPS', 'Reverse Camera', 'Sunroof', 'Child Seat', 'Roof Rack', 'Snow Chains', 'Toll Pass'].map(feat => (
                                            <div key={feat} className="col-md-3 col-6 mb-2">
                                                <label className="d-flex align-items-center gap-2 m-0 cursor-pointer text-secondary">
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.features.includes(feat)}
                                                        onChange={(e) => handleFeatureCheckbox(feat, e.target.checked)}
                                                        style={{ width: '15px', height: '15px' }}
                                                    />
                                                    <span className="small">{feat}</span>
                                                </label>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="ac-divider"></div>

                        {/* SECTION: Verification & Rego Documents */}
                        <div className="ac-section">
                            <div className="ac-section-header">
                                <div className="ac-section-icon" style={{ background: '#f59e0b', color: '#ffffff' }}><FaShieldAlt /></div>
                                <div>
                                    <h2>Verification &amp; Rego Documents</h2>
                                    <p>Registration number, VIN, and Insurance verification details</p>
                                </div>
                            </div>
                            <div className="ac-field-group">
                                <div className={`ac-field-wrap ${activeField === 'regoNumber' ? 'ac-field-active' : ''} ${formData.verification?.regoNumber ? 'ac-field-filled' : ''}`}>
                                    <label htmlFor="regoNumber">Rego / Registration Number</label>
                                    <input
                                        type="text"
                                        id="regoNumber"
                                        name="regoNumber"
                                        value={formData.verification?.regoNumber || ''}
                                        onChange={handleVerificationChange}
                                        onFocus={() => setActiveField('regoNumber')}
                                        onBlur={() => setActiveField(null)}
                                        placeholder="e.g. ABC-123"
                                    />
                                </div>
                                <div className={`ac-field-wrap ${activeField === 'regoState' ? 'ac-field-active' : ''} ${formData.verification?.regoState ? 'ac-field-filled' : ''}`}>
                                    <label htmlFor="regoState">Rego State</label>
                                    <select
                                        id="regoState"
                                        name="regoState"
                                        value={formData.verification?.regoState || 'NSW'}
                                        onChange={handleVerificationChange}
                                        onFocus={() => setActiveField('regoState')}
                                        onBlur={() => setActiveField(null)}
                                    >
                                        {['NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'ACT', 'NT'].map(st => (
                                            <option key={st} value={st}>{st}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className={`ac-field-wrap ${activeField === 'regoExpiry' ? 'ac-field-active' : ''} ${formData.verification?.regoExpiry ? 'ac-field-filled' : ''}`}>
                                    <label htmlFor="regoExpiry">Rego Expiry Date</label>
                                    <input
                                        type="date"
                                        id="regoExpiry"
                                        name="regoExpiry"
                                        value={formData.verification?.regoExpiry || ''}
                                        onChange={handleVerificationChange}
                                        onFocus={() => setActiveField('regoExpiry')}
                                        onBlur={() => setActiveField(null)}
                                    />
                                </div>
                                <div className={`ac-field-wrap ${activeField === 'vin' ? 'ac-field-active' : ''} ${formData.verification?.vin ? 'ac-field-filled' : ''}`}>
                                    <label htmlFor="vin">VIN / Chassis Number</label>
                                    <input
                                        type="text"
                                        id="vin"
                                        name="vin"
                                        value={formData.verification?.vin || ''}
                                        onChange={handleVerificationChange}
                                        onFocus={() => setActiveField('vin')}
                                        onBlur={() => setActiveField(null)}
                                        placeholder="e.g. 1HGCR2F8..."
                                    />
                                </div>
                                <div className={`ac-field-wrap ${activeField === 'insuranceProvider' ? 'ac-field-active' : ''} ${formData.verification?.insuranceProvider ? 'ac-field-filled' : ''}`}>
                                    <label htmlFor="insuranceProvider">Insurance Provider</label>
                                    <input
                                        type="text"
                                        id="insuranceProvider"
                                        name="insuranceProvider"
                                        value={formData.verification?.insuranceProvider || ''}
                                        onChange={handleVerificationChange}
                                        onFocus={() => setActiveField('insuranceProvider')}
                                        onBlur={() => setActiveField(null)}
                                        placeholder="e.g. NRMA Insurance"
                                    />
                                </div>
                                <div className={`ac-field-wrap ${activeField === 'insuranceExpiry' ? 'ac-field-active' : ''} ${formData.verification?.insuranceExpiry ? 'ac-field-filled' : ''}`}>
                                    <label htmlFor="insuranceExpiry">Insurance Expiry Date</label>
                                    <input
                                        type="date"
                                        id="insuranceExpiry"
                                        name="insuranceExpiry"
                                        value={formData.verification?.insuranceExpiry || ''}
                                        onChange={handleVerificationChange}
                                        onFocus={() => setActiveField('insuranceExpiry')}
                                        onBlur={() => setActiveField(null)}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="ac-divider"></div>

                        {/* SECTION 6: Images */}
                        <div className="ac-section">
                            <div className="ac-section-header">
                                <div className="ac-section-icon"><FaInfoCircle /></div>
                                <div>
                                    <h2>Car Images (Max 3 Photos • Max 10 MB each)</h2>
                                    <p>Manage and upload listing photos ({formData.images.length} / {MAX_PHOTOS} uploaded)</p>
                                </div>
                            </div>
                            <div className="ac-field-group ac-field-full">
                                <input
                                    type="file"
                                    id="carImagesInput"
                                    ref={fileInputRef}
                                    onChange={handleFileChange}
                                    multiple
                                    accept="image/*,.heic,.heif,.avif,.webp,.bmp,.tiff,.svg,.jfif"
                                    className="d-none"
                                />
                                <button 
                                    type="button"
                                    onClick={() => {
                                        if ((formData.images?.length || 0) >= MAX_PHOTOS) {
                                            toast.warning(`Maximum ${MAX_PHOTOS} photos are allowed per car listing!`);
                                            return;
                                        }
                                        fileInputRef.current.click();
                                    }}
                                    className="border-2 border-dashed border-gray-300 rounded-xl p-4 text-center hover:bg-light transition cursor-pointer mb-3 w-100"
                                    style={{ borderStyle: 'dashed', background: 'none' }}
                                >
                                    <span className="m-0 text-secondary font-weight-bold">Click here to upload car photos (Max 3 photos, up to 10 MB each)</span>
                                </button>

                                {formData.images.length > 0 && (
                                    <div className="d-flex flex-wrap gap-2">
                                        {formData.images.map((imgUrl, idx) => {
                                            const resolvedUrl = getFullImageUrl(imgUrl);
                                            return (
                                                <div key={idx} className="position-relative rounded-lg overflow-hidden border shadow-sm" style={{ width: '120px', height: '80px' }}>
                                                    <img src={resolvedUrl} alt="preview" className="w-full h-full object-cover" />
                                                    <button
                                                        type="button"
                                                        onClick={() => setFormData(prev => ({
                                                            ...prev,
                                                            images: prev.images.filter((_, i) => i !== idx)
                                                        }))}
                                                        className="position-absolute btn btn-danger btn-sm p-1 rounded-circle border-0"
                                                        style={{ top: '4px', right: '4px', width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px' }}
                                                    >
                                                        &times;
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="ac-divider"></div>

                        {/* SECTION 7: Description */}
                        <div className="ac-section">
                            <div className="ac-section-header">
                                <div className="ac-section-icon ac-section-icon-purple"><FaInfoCircle /></div>
                                <div>
                                    <h2>Additional Info</h2>
                                    <p>Provide short and full descriptions of this listing</p>
                                </div>
                            </div>
                            <div className="ac-field-group">
                                <div className={`ac-field-wrap ac-field-full ${activeField === 'shortDesc' ? 'ac-field-active' : ''} ${formData.shortDesc ? 'ac-field-filled' : ''}`}>
                                    <label htmlFor="shortDesc">Short Description *</label>
                                    <input type="text" id="shortDesc" name="shortDesc" value={formData.shortDesc} onChange={handleChange} required placeholder="Quick summary..." />
                                </div>
                                <div className={`ac-field-wrap ac-field-full ${activeField === 'description' ? 'ac-field-active' : ''} ${formData.description ? 'ac-field-filled' : ''}`}>
                                    <label htmlFor="description">Full Description</label>
                                    <textarea
                                        id="description"
                                        name="description"
                                        rows="5"
                                        value={formData.description}
                                        onChange={handleChange}
                                        onFocus={() => setActiveField('description')}
                                        onBlur={() => setActiveField(null)}
                                        placeholder="Complete listing description details..."
                                    ></textarea>
                                </div>
                            </div>
                        </div>

                        {/* Submit Footer */}
                        <div className="ac-form-footer">
                            <button type="button" className="ac-cancel-btn" onClick={() => navigate('/cars')}>
                                Cancel
                            </button>
                            <button type="submit" className="ac-submit-btn ac-submit-ready" disabled={loading}>
                                {loading ? 'Saving Changes...' : 'Save Changes'}
                            </button>
                        </div>

                    </form>
                </div>

            </div>
        </div>
    );
}

export default UpdateCar;
